/** @jsx jsx */
import { jsx, css } from "@emotion/core"

import { Icon, Modal, useAsyncEffect } from "@cd2/components/build/es6"
import React, { useState } from "react"
import { IRecord } from "../types/schema"
import { Grid, Spacer } from "@cd2/components"
import { IBranchSettings } from "../utils/useSettings"
import { createPortal } from "react-dom"
import { ProductLocationStockDisplaySwitcher } from "../apiHooks/productLocations"
import { icons } from "../constants/icons"

export function useOtherBranchStockModal(productId?: string) {
  const [isOpen, setIsOpen] = React.useState(false)

  function toggleModal() {
    setIsOpen(!isOpen)
  }

  const portalNode = document.getElementById("app-scene-portal-ref")!
  const node = createPortal(
    <Modal
      isOpen={isOpen}
      render={() => productId && <AllBranchStockDisplay productId={productId} />}
      onRequestClose={toggleModal}
    />,
    portalNode,
    "useOtherBranchStockModal",
  )

  return {
    toggleModal,
    node,
  }
}

function AllBranchStockDisplay({ productId }: { productId: string }) {
  const skeem = window.skeem
  const [allProductsLocations, setAllProductsLocations] = useState<
    { id: string; name: string; productLocationIds: string[] }[] | null
  >(null)

  useAsyncEffect(async () => {
    await fetchAllProductLocations()
  }, [productId])

  async function fetchAllProductLocations() {
    // independent lookups, so run them together; the product's own branch is dropped below
    const [product, stockLookupBranches] = await Promise.all([
      skeem.fetch2<IRecord<"products", "stock_code" | "manufacturerCode" | "branch">>({
        first: {
          name: "products",
          attributes: ["stock_code", "manufacturerCode", "branch"],
          filter: { matches: { id: productId } },
        },
      }),
      skeem.fetch2<
        (IRecord<"branches", "name" | "warehouses"> & {
          settings: IBranchSettings
        })[]
      >({
        model: {
          name: "branches",
          attributes: ["name", "settings", "warehouses"],
          filter: { matches: { appearsOnStockLookup: true } },
        },
      }),
    ])
    const branchSettings = stockLookupBranches.filter(branch => branch.id !== product.branch?.id)

    const allStockWarehouseIds = branchSettings
      .map(branch => {
        if (branch.settings.stockWarehouses?.length ?? 0 > 0) {
          return branch.settings.stockWarehouses.map(w => w.id)
        } else {
          return branch.warehouses.map(w => w.id)
        }
      })
      .flat()

    const branches = await skeem.fetch2<
      (IRecord<"branches", "name"> & { productLocationIds: string[] })[]
    >({
      model: {
        name: "branches",
        attributes: [
          "name",
          {
            as: "productLocationIds",
            value: {
              path: [
                {
                  name: "products",
                  filter: {
                    and: [
                      {
                        or: [
                          product?.stock_code && {
                            eq: [
                              { trim: { uppercase: { attr: "stock_code" } } },
                              { value: product?.stock_code.toUpperCase() },
                            ],
                          },
                          product?.manufacturerCode && {
                            eq: [
                              { trim: { uppercase: { attr: "manufacturerCode" } } },
                              { value: product?.manufacturerCode?.toUpperCase() },
                            ],
                          },
                        ].filter(Boolean),
                      },
                    ].filter(Boolean),
                  },
                },
                {
                  name: "product_locations",
                  filter: {
                    matches: {
                      warehouse: { or: allStockWarehouseIds },
                    },
                  },
                },
                "id",
              ],
            },
          },
        ],
        filter: { matches: { appearsOnStockLookup: true, id: { not: product.branch?.id } } },
      },
    })

    setAllProductsLocations(branches)
  }

  if (!allProductsLocations) {
    return (
      <div>
        <Icon icon={icons.spinner} spin />
        <span
          css={css`
            margin-left: 8px;
          `}
        >
          Loading
        </span>
      </div>
    )
  }

  const anyBranchesWithLocations = allProductsLocations.some(
    branch => branch.productLocationIds.length > 0,
  )

  if (!anyBranchesWithLocations) {
    return <div>No stock in other branches</div>
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <Grid
        columns={{ repeat: { size: { min: 200, max: { fr: 1 } }, times: "auto-fit" } }}
        gap={16}
      >
        {allProductsLocations
          .filter(branch => !!branch?.name && !branch.name.match(/cd2|test/i))
          .map(({ id, name, productLocationIds }) => (
            <div key={id}>
              <b>{name}</b>
              <Spacer />
              <Grid gap={4}>
                <ProductLocationStockDisplaySwitcher
                  productLocationIds={productLocationIds}
                  displayWithNoStock
                  showFull
                />
              </Grid>
            </div>
          ))}
      </Grid>
    </div>
  )
}
